import { __decorate } from "tslib";
import { LitElement, baseStyles } from '@vpbdevops/common';
import { html, literal } from '@vpbdevops/common/src/static-html.js';
import { classMap, ifDefined } from '@vpbdevops/common/src/directives.js';
import { property, query, state } from '@vpbdevops/common/src/decorators.js';
import styles from './button.scss.js';
export const ButtonVariants = ['filled', 'outlined', 'link', 'ghost'];
export const ButtonSizes = ['large', 'medium', 'small'];
export const ButtonTypes = ['button', 'submit', 'reset'];
export const ButtonTargets = ['_blank', '_parent', '_self', '_top'];
/**
 * A button is a clickable element used to perform an action. It contains a text label and a supporting icon can be displayed.
 * Text buttons might be supplemented with an icon to both clarify action and call attention to a button.
 * Icons are always positioned to the left or the right of the text label.
 *
 * @slot - The button's label
 * @slot prefix - A presentational prefix icon or similar element.
 * @slot suffix - A presentational suffix icon or similar element.
 *
 * @csspart button - The button HTML element, rendered either as `<a>` or `<button>`
 * @csspart label - The button's label
 * @csspart slot-prefix - The container that wraps the prefix.
 * @csspart slot-suffix - The container that wraps the suffix.
 */
export class VpbButton extends LitElement {
    constructor() {
        super();
        this.hasFocus = false;
        /**
         * The type of the button
         */
        this.variant = 'filled';
        /**
         * The size of the button
         */
        this.size = 'medium';
        /**
         * Disabled state for buttons.
         */
        this.disabled = false;
        /**
         * Active state for buttons.
         */
        this.active = false;
        this.hasPrefix = false;
        this.hasSuffix = false;
        this.addEventListener('click', this.proxyClick);
    }
    handleBlur() {
        if (this.disabled) {
            return;
        }
        this.hasFocus = false;
    }
    handleFocus() {
        if (this.disabled) {
            return;
        }
        this.hasFocus = true;
    }
    handleClick() {
        if (this.disabled) {
            return;
        }
        this.blur();
    }
    /**
     * Sets focus on the icon button.
     */
    focus(options) {
        this.button.focus(options);
    }
    /**
     * Removes focus from the icon button.
     */
    blur() {
        this.button.blur();
    }
    render() {
        const isLink = !!this.href;
        const tag = isLink ? literal `a` : literal `button`;
        /* eslint-disable lit/binding-positions,lit/no-invalid-html */
        return html `<${tag}
      part="button"
      class=${classMap({
            button: true,
            '-disabled': !isLink && this.disabled,
            '-active': this.active,
            '-focused': this.hasFocus,
            '-prefixed': this.variant !== 'link' && this.hasPrefix,
            '-suffixed': this.variant !== 'link' && this.hasSuffix,
            [`-${this.variant}`]: true,
            [`-${this.size}`]: true,
        })}
      type=${ifDefined(isLink ? this.type || undefined : this.type || 'button')}
      href=${ifDefined(isLink ? this.href : undefined)}
      target=${ifDefined(isLink ? this.target : undefined)}
      download=${ifDefined(isLink ? this.download : undefined)}
      rel=${ifDefined(isLink && this.target ? 'noreferrer noopener' : undefined)}
      role=${ifDefined(isLink ? undefined : 'button')}
      aria-disabled=${this.disabled ? 'true' : 'false'}
      tabindex=${this.disabled ? '-1' : '0'}
      @blur=${this.handleBlur}
      @focus=${this.handleFocus}
      @click=${this.handleClick}
    >
      <slot name="prefix" part="slot-prefix" class="prefix" @slotchange="${this.handlePrefixSlotChange}"></slot>
      <slot part="label" class="label"></slot>
      <slot name="suffix" part="slot-suffix" class="suffix" @slotchange="${this.handleSuffixSlotChange}"></slot>
    </${tag}>`;
    }
    proxyClick(e) {
        if (this.disabled) {
            e.preventDefault();
            e.stopImmediatePropagation();
            e.stopPropagation();
            return false;
        }
        const isLink = !!this.href;
        if (isLink) {
            this.button.click();
            return true;
        }
        if (this.type === 'submit' || this.type === 'reset') {
            const proxy = document.createElement('button');
            proxy.type = this.type;
            this.insertAdjacentElement('afterend', proxy);
            proxy.click();
            proxy.remove();
            return true;
        }
        return false;
    }
    handlePrefixSlotChange(e) {
        this.hasPrefix = e.target.assignedNodes().length > 0;
    }
    handleSuffixSlotChange(e) {
        this.hasSuffix = e.target.assignedNodes().length > 0;
    }
}
/**
 * DO NOT EDIT manually, this is being set automatically during release
 *
 * @private
 */
VpbButton.version = '0.13.0';
/** @private */
VpbButton.styles = [baseStyles, styles];
__decorate([
    query('.button')
], VpbButton.prototype, "button", void 0);
__decorate([
    state()
], VpbButton.prototype, "hasFocus", void 0);
__decorate([
    property()
], VpbButton.prototype, "variant", void 0);
__decorate([
    property()
], VpbButton.prototype, "size", void 0);
__decorate([
    property()
], VpbButton.prototype, "type", void 0);
__decorate([
    property()
], VpbButton.prototype, "target", void 0);
__decorate([
    property()
], VpbButton.prototype, "download", void 0);
__decorate([
    property()
], VpbButton.prototype, "href", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], VpbButton.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], VpbButton.prototype, "active", void 0);
__decorate([
    state()
], VpbButton.prototype, "hasPrefix", void 0);
__decorate([
    state()
], VpbButton.prototype, "hasSuffix", void 0);
//# sourceMappingURL=button.js.map